/* eslint-disable no-console */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserAddOutlined } from '@ant-design/icons';
import store from 'store';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import Box from 'ui-box';
import moment from 'moment';
import { Button, Divider, Space } from 'antd';
import InputSelectField from '../form-helpers/InputSelectField';
import InputField from '../form-helpers/InputField';
import PhoneInputField from '../form-helpers/PhoneInputField';
import { FormDatePicker } from '../form-helpers/FormField';
import LoadingSpinner from '../../common/LoadingSpinner';
import { errorNotification, successNotification } from '../../../utils/notification-helpers';
import { postPatientAPICall } from '../../../api/patient';
import { LOCAL_STORAGE_AUTH_KEY } from '../../../config/constants';

const formItemLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
}

const data = [
  {label: 'Family', value: 'Family'},
  {label: 'Friend', value: 'Friend'}
]

function AddFamilyMemberForm({ onSubmitBtnText }) {
  const { t } = useTranslation(['common', 'user-account', 'sign-up']);
  const [type, setType] = useState("screening")
  const history = useHistory();

  const onComplete = () => {
    successNotification(t("sign-up:guest.completeNotif"))
    history.push('/sign-up/guest/thank-you')
  }

  const FormSchema = useMemo(
    () =>
      Yup.object().shape({
        firstName: Yup.string().required(t('form.error.required')),
        lastName: Yup.string().required(t('form.error.required')),
        mobile: Yup.string().required(t('form.error.required')),
        relationship: Yup.string().required(t('form.error.required')),
        birthDate: Yup.string().required(t('form.error.required')),
      }),
    [t]
  );

  const initialValues = {
    firstName: '',
    lastName: '',
    mobile: '',
    relationship: '',
    birthDate: '',
  };

  return (
    <div style={{ width: '100%', textAlign: 'left' }}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={FormSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          const dataForm = { ...values };
          dataForm.dob = moment(dataForm.birthDate).format('YYYY-MM-DD')
          dataForm.owner_patient_ref = store.get(LOCAL_STORAGE_AUTH_KEY.GUEST_ID);
          dataForm.name = `${dataForm.firstName} ${dataForm.lastName}`
          const { responseOk, responseJSON } = await postPatientAPICall(dataForm);
          if (responseOk) {
            store.set(LOCAL_STORAGE_AUTH_KEY.FAMILY_PROFILE_SIGNUP, responseJSON)
            successNotification(t('sign-up:guest.addFamilyNotif', {"firstName": dataForm.name}));
            if (type === "screening") {
              history.push('/sign-up/guest/screening-request')
            }

            if (type === "add") {
              history.push('/sign-up/guest/add-family-member')
            }

            setSubmitting(false);
            resetForm({ values: initialValues });
          } else {
            errorNotification(t('form.error.submit'));
          }
        }}
      >
        {({ isSubmitting, isValid, setFieldValue, handleSubmit }) => {
          return (
            <Form>
              <fieldset disabled={isSubmitting}>
                <Box marginBottom={35}>
                  <InputField
                    type="text"
                    name="firstName"
                    label={t('user-account:personalInformation.form.firstName.label')}
                    placeholder={t('user-account:personalInformation.form.firstName.placeholder')}
                  />
                  <InputField
                    type="text"
                    name="lastName"
                    label={t('user-account:personalInformation.form.lastName.label')}
                    placeholder={t('user-account:personalInformation.form.lastName.placeholder')}
                  />
                  <PhoneInputField
                    name="mobile"
                    label={t('user-account:personalInformation.form.phoneNumber.label')}
                    placeholder={t('user-account:personalInformation.form.phoneNumber.placeholder')}
                  />
                  <InputSelectField
                    name="relationship"
                    label={t('sign-up:guest.form.relation.label')}
                    placeholder={t('sign-up:guest.form.relation.placeholder')}
                    options={data}
                  />
                  <FormDatePicker
                    type="date"
                    name="birthDate"
                    defaultValue={moment()}
                    format="YYYY/MM/DD"
                    style={{ width: '100%', padding: '3%' }}
                    formItemLayout={formItemLayout}
                    onChange={(e) => setFieldValue('birthDate', e)}
                    label={t('user-account:personalInformation.form.dateBirth.label')}
                    placeholder={t('user-account:personalInformation.form.dateBirth.placeholder')}
                    disabledDate={current => {
                      return current && current > moment();
                    }}
                  />
                  <Box paddingTop={15}>
                    <Button
                      disabled={isSubmitting || !isValid}
                      loading={isSubmitting}
                      htmlType="button"
                      type="primary"
                      size="large"
                      block
                      onClick={() => {
                        handleSubmit()
                        setType("screening")
                      }}
                    >
                      {isSubmitting ? <LoadingSpinner position="full" transparent={false} /> : onSubmitBtnText}
                    </Button>
                  </Box>
                </Box>
                <Box marginBottom={35}>
                  <Divider />
                </Box>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    disabled={isSubmitting || !isValid}
                    type="primary"
                    size="large"
                    loading={isSubmitting}
                    htmlType="button"
                    block
                    ghost
                    icon={<UserAddOutlined />}
                    onClick={() => {
                      handleSubmit()
                      setType("add")
                    }}
                  >
                    {isSubmitting ? <LoadingSpinner position="full" transparent={false} /> : t('sign-up:guest.addAnotherFamilyMember')}
                  </Button>
                  <Button
                    htmlType="button"
                    type="primary"
                    size="large"
                    block
                    onClick={() => {
                      handleSubmit()
                      setType("complete")
                      onComplete()
                    }}
                  >
                    {isSubmitting ? <LoadingSpinner position="full" transparent={false} /> : t('sign-up:guest.complete')}
                  </Button>
                </Space>
              </fieldset>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

AddFamilyMemberForm.defaultProps = {
  onSubmitBtnText: 'Confirm & Save Changes',
};

const { string } = PropTypes;

AddFamilyMemberForm.propTypes = {
  onSubmitBtnText: string
};

export default AddFamilyMemberForm;
